import { useState, useCallback, useRef } from "preact/hooks";
import { parseSSELine, type ChatMessage } from "./useChat.js";
import { API_BASE } from "../lib/api-base.js";
const CHAT_APPROVAL_TIMEOUT = 5000;

/**
 * Request approval from the parent admin page before sending a chat.
 * This gives the parent a chance to auto-save any unsaved draft config
 * so the sandbox API uses the latest settings.
 * Returns a fresh access token if the parent provides one.
 */
function requestChatApproval(): Promise<string | null> {
  return new Promise((resolve) => {
    let resolved = false;
    function handler(event: MessageEvent) {
      if (event.source !== window.parent) return;
      if (event.data?.type === "canary:chat-approved") {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("message", handler);
          resolve(event.data.accessToken || null);
        }
      }
    }
    window.addEventListener("message", handler);
    window.parent.postMessage({ type: "canary:chat-starting" }, "*");
    // Timeout: proceed with current token if parent doesn't respond
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener("message", handler);
        resolve(null);
      }
    }, CHAT_APPROVAL_TIMEOUT);
  });
}

export function usePreviewChat(tenantId: string, accessToken: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming || !tenantId || !accessToken) return;

      const userMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: "user",
        content: text.trim(),
      };

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_a`,
        role: "assistant",
        content: "",
        streaming: true,
      };

      const newMessages = [...messages, userMsg];
      setMessages([...newMessages, assistantMsg]);
      setStreaming(true);
      setSuggestions([]);

      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 60_000);

      try {
        // Wait for parent to auto-save draft if needed; may return a fresh token
        const freshToken = await requestChatApproval();
        const tokenToUse = freshToken || accessToken;

        const res = await fetch(
          `${API_BASE}/api/admin/tenants/${tenantId}/sandbox/chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenToUse}`,
            },
            body: JSON.stringify({
              tenant_id: tenantId,
              messages: newMessages.map((m) => ({
                role: m.role,
                content: m.content,
              })),
            }),
            signal: controller.signal,
          }
        );

        if (!res.ok || !res.body) {
          // Surface the server's message (e.g. the daily playground cap 429)
          // instead of a generic failure.
          let serverMessage = "";
          try {
            const body = await res.json();
            if (typeof body?.error === "string") serverMessage = body.error;
          } catch { /* non-JSON body */ }
          const err = new Error(serverMessage || "Stream request failed");
          (err as any).userFacing = !!serverMessage;
          throw err;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const event = parseSSELine(line.trim());
            if (!event) continue;

            if (event.type === "delta") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + event.text,
                  };
                }
                return updated;
              });
            }

            if (event.type === "replace") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.text,
                  };
                }
                return updated;
              });
            }

            if (event.type === "status") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant" && !last.content) {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.text,
                    streaming: true,
                  };
                }
                return updated;
              });
            }

            if (event.type === "error") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.message || "Something went wrong",
                    streaming: false,
                  };
                }
                return updated;
              });
            }

            if (event.type === "done") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    streaming: false,
                    sources: Array.isArray(event.sources) ? event.sources : undefined,
                  };
                }
                return updated;
              });
            }

            if (event.type === "suggestions") {
              if (Array.isArray(event.suggestions) && event.suggestions.length > 0) {
                setSuggestions(event.suggestions);
              }
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          // Timeout abort: finalize the message so it doesn't stay flagged as
          // streaming (stuck typing indicator when no content arrived). The
          // clearMessages abort is a no-op here — the list is already empty.
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              updated[updated.length - 1] = {
                ...last,
                content: last.content || "The response timed out. Please try again.",
                streaming: false,
              };
            }
            return updated;
          });
          return;
        }
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === "assistant") {
            updated[updated.length - 1] = {
              ...last,
              content: err?.userFacing && err.message
                ? err.message
                : "Something went wrong. Please try again.",
              streaming: false,
            };
          }
          return updated;
        });
      } finally {
        clearTimeout(timeoutId);
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [tenantId, accessToken, streaming, messages]
  );

  const clearMessages = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    setMessages([]);
    setStreaming(false);
    setSuggestions([]);
  }, []);

  return { messages, streaming, sendMessage, clearMessages, suggestions };
}
